import { useState, useEffect, useRef, Fragment } from 'react';
import PaginationLink from '../shared/PaginationLink';
import {
  buildPageHref,
  buildSearchHref,
  getPageFromQuery,
  getSearchFromQuery,
  hasMoreAlgoliaPages,
  stripListingParams,
} from '../../util/PaginationUtil';
import {
  assemble,
  correctInternalLink,
  createFilterData,
  createFilterDataProductFamily,
  getAlgoliaDatasetValue,
  increaseUntilDivisibleByThree,
  isItUndefined,
  isLinkAbsolute,
  marketoOptions as options,
  showPart,
  stripHtml,
  getAlgoliaIndex,
  handleGoTo,
  modifyLinks,
  createFiltersFromRedirect,
  truncateText,
  getTarget,
  pushSearchSuggestionClickEvent,
} from '../../util/DataUtil';
import { useRouter } from 'next/router';
import { getLanguage, useWindowSize } from '../../util/PageUtil';
import { brandColor, getClassTextForColor } from '../../util/ColorUtil';
import ResourceFilter from '../resources/resource-filter';
import { useDispatch, useSelector } from 'react-redux';
import { setLocalStorageRedux } from '../../store/localStorageSlice';
import { correctUrlFormatForRouting } from '../../util/DataUtil';
import CertificationItem from './CertificationItem';
import Link from 'next/link';
import { SearchBarPlacement } from '../../constants/SearchBarPlacement';
import { FilterName } from '../../constants/FilterName';
import {
  sendClickedObjectIDsAfterSearch,
  sendNoResultsEvent,
} from '../../util/AlgoliaInsights';

const onMouseEnter = (item, setHover) => {
  setHover(item);
};

const onMouseLeave = (item, setHover) => {
  setHover(false);
};

// Advances the page instead of growing the page size.
//
// This used to call `setHitsPerPage(hitsPerPage + 9)`, so there was never a
// page two — only an ever-larger page one — and nothing for a crawler to
// follow. `fetchAlgoliaPagination` already existed and already appends, so the
// machinery for real pagination was there; only this handler was bypassing it.
const handleLoadMore = (page, setPage) => {
  setPage(page + 1);
};

const handleShowLess = (
  page,
  setPage,
  setHitsPerPage,
  hitsPerPage,
  anchorToTop,
  setAnchorToTop
) => {
  if (page > 0) {
    setPage(0);
    setAnchorToTop(anchorToTop + 1);
  }
};

// Show All loads the whole list in one request and puts the last page of the
// series in the URL, so the address says what is on screen and the link can be
// shared or reloaded.
//
// The fetch itself stays on page zero with a page size big enough to cover
// everything — `page` and `hitsPerPage` multiply into the Algolia offset, so
// raising both at once would ask for a slice far past the end of the results.
/** The last page of a series, 0-based. */
const lastPageOf = (algoliaData, perPage) => {
  const total = algoliaData?.nbHits;

  return typeof total === 'number' && total > 0
    ? Math.max(0, Math.ceil(total / perPage) - 1)
    : 0;
};

// Loads the whole list in one request. The URL is handled by the link wrapping
// this, which points at the last page of the series.
//
// The fetch deliberately stays on page zero with a page size big enough to
// cover everything: `page` and `hitsPerPage` multiply into the Algolia offset,
// so raising both at once would ask for a slice far past the end.
const handleShowAll = (setPage, setHitsPerPage) => {
  setPage(0);
  setHitsPerPage(10000);
};

//function for making product card to be right clickable
const handleContextMenu = (e, url, locale = 'en') => {
  e.preventDefault();

  if (url) {
    let absoluteUrl = url;

    // Check if the url is a full URL
    if (!url.startsWith('http')) {
      // If it's not a full URL, construct the absolute URL using the current origin
      absoluteUrl =
        locale !== 'en'
          ? `${window.location.origin}/${locale}${url}`
          : `${window.location.origin}${url}`;
    } else {
      // If it's a full URL, extract the path and construct the absolute URL
      const path = new URL(url).pathname;
      absoluteUrl = window.location.origin + path;
    }
    window.open(absoluteUrl, '_blank');
  } else {
    console.error('Internal link is not valid:', url);
  }
};

const renderCards = (
  cards,
  router,
  hover,
  setHover,
  backgroundColor,
  locale
) => {
  if (!cards) {
    return;
  }

  return cards.map((item, key) => {
    if (
      isItUndefined(item?.field_card_title) &&
      isItUndefined(item?.field_card_description)
    ) {
      return (
        <div
          key={`cards-product-family-key-${item?.field_card_title?.[0]?.value || key}`}
          className={'div-product-family-link card'}
          onMouseEnter={() => onMouseEnter(item, setHover)}
          onMouseLeave={() => onMouseLeave(item, setHover)}
          onClick={() =>
            handleGoTo(isItUndefined(item?.field_card_url), router)
          }
          style={{ backgroundColor: backgroundColor }}
          onContextMenu={(e) =>
            handleContextMenu(e, item?.field_card_url?.[0]?.value, locale)
          }
        >
          <div className={'card-inner-container'}>
            <div>
              <h3 className={'card-title'}>
                {isItUndefined(item?.field_card_title)}
              </h3>
              <p
                className={'card-description'}
                dangerouslySetInnerHTML={{
                  __html: stripHtml(
                    isItUndefined(item?.field_card_description)
                  ),
                }}
              />
            </div>
            {/*{*/}
            {/*    hover.id === item.id ? <img src={'/images/swap.svg'} alt={'arrows'} /> : <img src={'/images/doublearrows.svg'} alt={'arrows'} />*/}
            {/*}*/}
            <img
              className={'top'}
              src={'/images/doublearrows.svg'}
              alt={'arrows'}
            />
            <img className={'bottom'} src={'/images/swap.svg'} alt={'arrows'} />
          </div>
        </div>
      );
    }
  });
};

const renderCertifications = (certifications) => {
  if (!certifications) return null;

  if (typeof certifications === 'string') {
    return <CertificationItem text={certifications} />;
  }

  return certifications.map((txt, i) => (
    <CertificationItem key={`certification-${txt}`} text={txt} />
  ));
};

const renderAlgoliaCards = (
  cards,
  router,
  hover,
  setHover,
  algoliaData,
  backgroundColor,
  page,
  setPage,
  clearAll,
  setHitsPerPage,
  setAnchorToTop,
  anchorToTop,
  hitsPerPage,
  isFilterActive,
  data,
  lang,
  locale,
  query,
  indexName
) => {
  if (!cards) {
    return;
  }

  let result = [];

  let numberOfIterations = increaseUntilDivisibleByThree(cards.length) - 1;
  if (hitsPerPage !== 8) {
    if (numberOfIterations == -1) {
      numberOfIterations = 2;
    }
  }
  for (let item in cards) {
    const formattedUrl = correctUrlFormatForRouting(cards[item]?.url);
    result.push(
      <div
        key={item}
        className={'div-product-family-link card'}
        onMouseEnter={() => onMouseEnter(item, setHover)}
        onMouseLeave={() => onMouseLeave(item, setHover)}
        onClick={() => {
          pushSearchSuggestionClickEvent(
            Array.isArray(query)
              ? (query[0]?.toLowerCase() ?? '')
              : (query?.toLowerCase() ?? ''),
            SearchBarPlacement.PRODUCT_FAMILY,
            cards[item]?.category_name + ' ' + cards[item]?.title,
            Number(item) + 1
          );
          sendClickedObjectIDsAfterSearch(
            algoliaData.queryID,
            cards[item]?.objectID,
            indexName,
            Number(item) + 1
          );
          handleGoTo(cards[item]?.url, router, lang);
        }}
        onContextMenu={(e) => handleContextMenu(e, formattedUrl.url, locale)}
        style={{ backgroundColor: backgroundColor }}
      >
        <div className={'card-inner-container'}>
          <div>
            <h3 className={'card-title'}>
              {cards[item]?.category_name} {cards[item]?.title}
            </h3>
            <p
              className={'card-description'}
              dangerouslySetInnerHTML={{
                __html: stripHtml(cards[item]?.field_address_location),
              }}
            />
            <div className={'certifications-wrapper'}>
              {renderCertifications(cards[item]?.primary_source_language)}
            </div>
          </div>
          {/*<img className={'top'} src={'/images/doublearrows.svg'} alt={'arrows'} />*/}
          <img className={'bottom'} src={'/images/swap.svg'} alt={'arrows'} />
        </div>
      </div>
    );
  }

  if (cards.length == algoliaData.nbHits || isFilterActive) {
    for (let index = 0; index < numberOfIterations; index++) {
      result.push(
        <div
          key={`blank-${index}`}
          className={'div-product-family-link card'}
          style={{ backgroundColor: backgroundColor, cursor: 'default' }}
          //  onContextMenu={(e) => handleContextMenu(e, item)}
        >
          <div className={'card-inner-container'}>
            <div>
              <h3 className={'card-title'}></h3>
              <p
                className={'card-description'}
                dangerouslySetInnerHTML={{ __html: '' }}
              />
              <div className={'certifications-wrapper'}></div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Whether to show the pagination controls at all.
  //
  // This used to be `cards.length >= 8` — "a full page, so there is probably
  // more". That stopped holding once `?page=N` started rendering page N on its
  // own: the last page of a series is nearly always short, so reloading
  // `?page=33` dropped the controls entirely and left no way back to the top
  // of the list.
  //
  // The two questions are asked directly instead: is there a page after this
  // one, and is there one before it.
  const hasNextPage =
    hasMoreAlgoliaPages(algoliaData) && algoliaData.nbHits > 8;
  const hasPreviousPage = page > 0 || hitsPerPage !== 8;

  if (cards.length > 0 && (hasNextPage || hasPreviousPage)) {
    result.push(
      <div key="load-more" className={'card'}>
        <div className={'load'}>
          {hasNextPage ? (
            <Fragment>
              <PaginationLink
                page={page + 1}
                onLoadMore={setPage}
                className={'text'}
              >
                {isItUndefined(data?.global?.field_load_more_text)}
              </PaginationLink>
              {
                <PaginationLink
                  className={'show-all'}
                  rel="last"
                  page={lastPageOf(algoliaData, 8)}
                  onLoadMore={() => handleShowAll(setPage, setHitsPerPage)}
                >
                  {isItUndefined(data?.global?.field_show_all_text_translation)}
                </PaginationLink>
              }
            </Fragment>
          ) : (
            <Fragment>
              {/*<p className={'text'}>Show Less</p>*/}
              <p
                className={'text'}
                onClick={() => {
                  clearAll();
                  setAnchorToTop(anchorToTop + 1);
                }}
              >
                {isFilterActive
                  ? isItUndefined(data?.global?.field_celar_filte?.text)
                  : isItUndefined(data?.global?.field_show_less_text)}
              </p>
              {isFilterActive ? (
                <PaginationLink
                  className={'show-all'}
                  rel="first"
                  page={0}
                  onLoadMore={() =>
                    handleShowLess(
                      page,
                      setPage,
                      setHitsPerPage,
                      hitsPerPage,
                      anchorToTop,
                      setAnchorToTop
                    )
                  }
                >
                  {isItUndefined(data?.global?.field_show_less_text)}
                </PaginationLink>
              ) : (
                <></>
              )}
            </Fragment>
          )}
        </div>
      </div>
    );
  }

  return result;
};

const ProductFamily = (data) => {
  if (!data) {
    return;
  }
  const router = useRouter();

  // Server-rendered first slice, so the cards and the link to the next page are
  // in the source HTML rather than appearing only after hydration.
  const serverResults = data?.initialProductFamily;
  const serverHits = Array.isArray(serverResults?.hits)
    ? serverResults.hits
    : [];

  const [hover, setHover] = useState(false);
  const [algoliaData, setAlgoliaData] = useState(serverResults || []);
  const [facetFilters, setFacetFilters] = useState([]);
  // Seeded from the URL so a reload of `?page=3` stays on page three.
  const [page, setPage] = useState(() => getPageFromQuery(router?.query));
  const [showMore, setShowMore] = useState(serverHits);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filterProps, setFilterProps] = useState([]);
  const [languageFilter, setLanguageFilter] = useState([]);
  const [applyFilter, setApplyFilter] = useState(false);
  // Seeded from the URL — see the note in handleSearch.
  const [query, setQuery] = useState(() => {
    const term = getSearchFromQuery(router?.query);
    return term ? [term] : [];
  });
  const [filter, setFilter] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hitsPerPage, setHitsPerPage] = useState(8);

  const { locale } = router;
  const windowSize = useWindowSize();
  const [anchorToTop, setAnchorToTop] = useState(0);
  const myLocalStorage = useSelector(setLocalStorageRedux);

  let lng = data.lang;
  let facetFiltersArray = [];
  let categoryName = [];
  let lngArray = [];
  let algoliaDataset =
    getAlgoliaDatasetValue(
      isItUndefined(data.children?.field_choose_algolia_dataset)
    ) + getAlgoliaIndex();

  let state = {
    backgroundColor: brandColor(
      isItUndefined(data.children?.field_background_color)
    ),
    textColor: getClassTextForColor(
      isItUndefined(data.children?.field_background_color)
    ),
    mainTitle: isItUndefined(data.children?.field_product_family_main_title),
    topLevelTitle: isItUndefined(data.children?.field_top_level_title),
    topLevelDescription: isItUndefined(
      data.children?.field_top_level_description
    ),
    url: isItUndefined(data.children?.field_top_level_url, 'uri'),
    urlTitle: isItUndefined(data.children?.field_top_level_url, 'title'),
    useAlgoliaData: isItUndefined(data.children.field_use_algolia_data),
    cards: data.children?.field_card?.product_family_card,
    algoliaCards: showMore,
    filterCategories: data.categories,
    filterLocations: data.locations,
  };

  // The first slice is already rendered from the server; refetching it on mount
  // would only replace it with itself.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialFetch = useRef(serverHits.length > 0 && !urlNarrowsResults);

  // Three separate effects call fetchAlgoliaData on mount — this one, the
  // filter effect, and the hitsPerPage effect. Each replaces the results, so
  // guarding only one still let the other two overwrite what the server
  // rendered for the page in the URL.
  //
  // The flag stays raised for the whole first pass and is lowered by the effect
  // declared last, which React runs after all of them. A guard that cleared
  // itself on first use would only ever stop the first effect.
  useEffect(() => {
    if (skipInitialFetch.current) {
      return;
    }
    setIsLoading(true);
    fetchAlgoliaData();
  }, []);

  useEffect(() => {
    if (anchorToTop > 0) {
      let doc = document.getElementById('product-family');
      window.scrollTo({ top: doc?.offsetTop - 150, behavior: 'smooth' });
    }
  }, [anchorToTop]);

  useEffect(() => {
    if (!lng) {
      return;
    }

    if (state.useAlgoliaData === '1') {
      if (skipInitialFetch.current) {
        return;
      }
      fetchAlgoliaData();
    }
  }, [applyFilter, query, filter]);

  const skipInitialPagination = useRef(true);

  useEffect(() => {
    // On mount the effect that loads results has already fetched the page the
    // URL asked for. This one appends, so letting its first run through would
    // show that page's results twice.
    if (skipInitialPagination.current) {
      skipInitialPagination.current = false;
      return;
    }

    if (page > 0) {
      fetchAlgoliaPagination();
    } else if (page === 0) {
      fetchAlgoliaData();
    }
  }, [page]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      return;
    }
    fetchAlgoliaData();
  }, [hitsPerPage]);

  // Declared after every effect that reads the flag, so it runs last on mount.
  useEffect(() => {
    skipInitialFetch.current = false;
  }, []);

  // Hydrating the filters out of the URL is a one-time thing. This effect
  // re-runs whenever its dependencies settle, and on a later run it would read
  // the URL again and re-apply it on top of whatever the visitor had since
  // selected — overwriting their choice with a fresh mapping.
  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    // let localFilter = router.query.type !== undefined ? router.query.type :
    //     router.query.category !== undefined ? router.query.category : '';

    // `page` is in the query string but is not a filter. Letting it through
    // makes the check below think the visitor arrived with filters applied,
    // which resets the page to one — so reloading `?page=2` showed page one.
    let localFilter = stripListingParams(router.query);

    if (typeof window !== 'undefined') {
      // localFilter.length > 0 ? triggerFilterFromRedirect([{name: localFilter}]) : clearAll()
      //
      // The URL still carries filters, so there is nothing to clear — whether
      // or not this run is the one that hydrated them. Reaching the clear
      // branch on a later run undid the filter the URL had just asked for.
      if (hasNonEmptyProperties(localFilter)) {
        if (!hydratedFromUrl.current) {
          hydratedFromUrl.current = true;
          triggerFilterFromRedirect(localFilter);
        }
      } else if (filterProps.length > 0 || query.length > 0) {
        // Only clear when something is actually applied.
        //
        // `clearAll` resets the page through `triggerFilter([])`, and this
        // effect re-runs whenever its dependencies settle — the author list
        // arriving, for instance. Guarding only the first run was not enough:
        // the second run wiped the page the URL had asked for, so after a
        // reload of `?page=3` the See More link pointed back at page two.
        //
        // With nothing applied there is nothing to clear, so the page the
        // visitor asked for survives.
        clearAll();
      }

    }
  }, [myLocalStorage]);

  const fetchAlgoliaData = () => {
    // let url = createFilterData(lngArray, facetFiltersArray, facetFilters, query, 8, page, lng, algoliaDataset);
    let url = createFilterDataProductFamily(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      algoliaDataset,
      categoryName
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setShowMore(newResponse.hits);
          setAlgoliaData(newResponse);
          setIsLoading(false);
          if (newResponse.hits.length === 0 && query.length > 0) {
            sendNoResultsEvent(newResponse.queryID, algoliaDataset);
          }
        });
    };
    fetchAlgolia();
  };

  const fetchAlgoliaPagination = () => {
    // let url = createFilterData(lngArray, facetFiltersArray, facetFilters, query, 100000, page, lng, algoliaDataset);
    let url = createFilterDataProductFamily(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      8,
      page,
      lng,
      algoliaDataset,
      categoryName
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setShowMore((previousResponse) => [
            ...previousResponse,
            ...newResponse.hits,
          ]);
          setAlgoliaData(newResponse);
        });
    };

    fetchAlgolia();
  };

  const triggerFilterFromRedirect = (filter) => {
    let filters = createFiltersFromRedirect(filter, data?.categories);
    triggerFilter(filters, false);
    setApplyFilter(true);
    setQuery([]);
  };

  function hasNonEmptyProperties(obj) {
    for (const key in obj) {
      if (
        key !== 'url' &&
        obj[key] !== null &&
        obj[key] !== undefined &&
        obj[key] !== ''
      ) {
        return true;
      }
    }
    return false;
  }

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      // searchFilters.push(filter.field_source_translation);
      searchFilters.push(filter);
    }

    setFacetFilters(searchFilters);
    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
    setHitsPerPage(8);
    setApplyFilter(false);
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    // setFilterProps([]);
    // setFacetFilters([]);
    setPage(0);
    setApplyFilter(false);
    // Put the term in the address bar so a reload or a shared link restores the
    // same results and pagination composes with it. `buildSearchHref` drops the
    // page: a new search starts at the beginning.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const clearQuery = () => {
    setQuery([]);
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setIsSearchActive(false);
    setIsFilterActive(true);
  };

  const clearAll = () => {
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    // setQuery([]);
    if (query.length > 0) {
      setQuery([]);
    }
    setShowMore([]);
    setFilter(!filter);
    setPage(0);
    setHitsPerPage(8);
    setIsFilterActive(false);

    // Take the page out of the address too. This is what Show Less runs, and
    // Show All puts the last page of the series in the URL — so without this
    // the list collapsed back to eight items while the address still claimed
    // `?page=33`, and reloading re-expanded it.
    //
    // Only when there is a page to drop: an unconditional rewrite races with
    // the filter control's own push and wipes the filter back out.
    if (getPageFromQuery(router?.query) > 0) {
      router.replace(buildPageHref(router.asPath, 0), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const lightenDarkenColor = (hex, lum) => {
    // validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, '');
    if (hex.length < 6) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    lum = lum || 0;

    // convert to decimal and change luminosity
    var rgb = '#',
      c,
      i;
    for (i = 0; i < 3; i++) {
      c = parseInt(hex.substr(i * 2, 2), 16);
      c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
      rgb += ('00' + c).substr(c.length);
    }

    return rgb;
  };

  return (
    <div
      id={'product-family'}
      style={{ backgroundColor: state.backgroundColor }}
      className={state.textColor}
    >
      {state.useAlgoliaData === '1' && (
        <ResourceFilter
          metrosLabel={isItUndefined(data.global.field_metro_label) || 'Metros'}
          clearQuery={clearQuery}
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          setIsSearchActive={setIsSearchActive}
          setIsFilterActive={setIsFilterActive}
          search={isItUndefined(data.global.field_search_text_translation)}
          // title={isItUndefined(data.field_filter_resources)}
          filterProps={filterProps}
          triggerFilter={triggerFilter}
          data={assemble(data.categories)}
          onSearchChange={handleSearch}
          handleFilterApply={setApplyFilter}
          searchQuery={query}
          setQuery={setQuery}
          languageFilter={languageFilter}
          setLanguageFilter={setLanguageFilter}
          screenWidth={windowSize.width}
          clearAll={clearAll}
          title={isItUndefined(data.global.field_filter_text)}
          setFilter={setFilter}
          filter={filter}
          cancelText={isItUndefined(data.global.field_cancel_text_translation)}
          applyFilterText={isItUndefined(data.global.field_apply_filters_text)}
          clearAllText={isItUndefined(data.global.field_clear_all_text)}
          selectedText={isItUndefined(data.global.field_selected_text)}
          seeMoreText={isItUndefined(data?.global?.field_see_more_text)}
          seeLessText={isItUndefined(data?.global?.field_see_less)}
          singleFilter={true}
          placement={SearchBarPlacement.PRODUCT_FAMILY}
          dataLayerFilterName={FilterName.PRODUCT_FAMILY}
        />
      )}
      {showPart(state.mainTitle) && (
        <div className={'inner-container'}>
          <div className={'title'}>
            <span
              dangerouslySetInnerHTML={{
                __html: modifyLinks(state.mainTitle, locale),
              }}
            />
          </div>
        </div>
      )}
      {state.useAlgoliaData === '0' &&
        showPart(
          state.topLevelTitle,
          state.topLevelDescription,
          state.url,
          state.urlTitle
        ) && (
          <div
            className={`top-level ${state.url === undefined ? 'top-level-no-hover' : ''}`}
            onClick={() => state.url && handleGoTo(state.url, router, lng)}
          >
            <div className={'inner-container'}>
              {showPart(state.topLevelTitle) && (
                <h2
                  className={'top-level-title'}
                  dangerouslySetInnerHTML={{
                    __html: stripHtml(state.topLevelTitle),
                  }}
                />
              )}
              <div className={'top-level-right'}>
                {showPart(state.topLevelDescription) && (
                  <p
                    className={'top-level-description'}
                    dangerouslySetInnerHTML={{
                      __html: stripHtml(state.topLevelDescription),
                    }}
                  />
                )}
                {showPart(state.url, state.urlTitle) && (
                  <Link href={correctInternalLink(state.url)} passHref>
                    <a
                      className={'a-product-family-link top-level-link'}
                      target={getTarget(correctInternalLink(state.url))}
                    >
                      {state.urlTitle}
                      <img
                        width={'auto'}
                        height={'auto'}
                        src={'/images/arrow-right-white.svg'}
                        alt={'scroll-arrow'}
                      />
                    </a>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      <div
        className={'cards'}
        style={{
          backgroundColor: lightenDarkenColor(state.backgroundColor, -0.1),
        }}
      >
        {state.useAlgoliaData === '1' && isLoading === false ? (
          showMore?.length === 0 ? (
            <div
              className={'no-results-wrapper'}
              style={{ backgroundColor: state.backgroundColor }}
            >
              <div className={'inner-container'}>
                <div className={'no-results'}>
                  {isItUndefined(data.global.field_no_results_text)
                    ? isItUndefined(data.global.field_no_results_text)
                    : 'No results were found. Please check the spelling, reset any filters and try again.'}
                </div>
              </div>
            </div>
          ) : (
            renderAlgoliaCards(
              showMore,
              router,
              hover,
              setHover,
              algoliaData,
              state.backgroundColor,
              page,
              setPage,
              clearAll,
              setHitsPerPage,
              setAnchorToTop,
              anchorToTop,
              hitsPerPage,
              isFilterActive,
              data,
              lng,
              locale,
              query,
              algoliaDataset
            )
          )
        ) : (
          renderCards(
            state.cards,
            router,
            hover,
            setHover,
            state.backgroundColor,
            locale
          )
        )}
      </div>
    </div>
  );
};

export default ProductFamily;
